import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClassNames, DayPicker } from "react-day-picker";

import {
    ArrowLeftV2Icon,
    ArrowRightV2Icon,
    CalendarIcon,
} from "../../assets/img/icons";
import Modal from "../Modal";
import Text from "../Text";

import { Placement } from "@floating-ui/react";
import { fr } from "date-fns/locale";
import {
    getFirstDayOfSpecificMonth,
    getFirstDayOfSpecificYear,
    getFirstDayOfWeek,
    getLastDayOfSpecificMonth,
    getLastDayOfSpecificYear,
    getMonthYearLabel,
    getShortDateFormat,
    getShortFrDate,
} from "../../utils/date";
import Button from "../Button";
import Divider from "../Divider";
import { Dropdown } from "../Dropdown";
import FieldError from "../FieldError";
import IconButton from "../IconButton";
import styles from "./styles.module.scss";

type CommonProps = {
    classNameTrigger?: string;
    placement?: Placement;
    error?: string;
};

interface DatePickerProps<M extends "single" | "range"> {
    mode: M;
    date?: M extends "single" ? Date : never;
    setDate?: M extends "single" ? (date: Date) => void : never;
    onBlur?: M extends "single" ? () => void : never;
    disableWeekends?: M extends "single" ? boolean : never;
    startDate?: M extends "range" ? Date : never;
    setStartDate?: M extends "range" ? (date: Date) => void : never;
    endDate?: M extends "range" ? Date : never;
    setEndDate?: M extends "range" ? (date: Date) => void : never;
    min?: Date;
    max?: Date;
}

const DatePicker = <M extends "single" | "range">({
    mode,
    date,
    setDate,
    onBlur,
    disableWeekends,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    classNameTrigger,
    placement = "bottom",
    min,
    max,
    error,
}: DatePickerProps<M> & CommonProps) => {
    const [open, setOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dateNow = useMemo(() => new Date(), []);
    const triggerRef = useRef<HTMLDivElement | null>(null);
    const [showMobileRangeText, setShowMobileRangeText] = useState(true);

    const applyTempSelection = () => {
        if (mode === "range" && tempStart && tempEnd) {
            setStartDate?.(tempStart);
            setEndDate?.(tempEnd);
        }
    };

    const handleClose = () => {
        setOpen(false);
        if (mode === "single" && onBlur) {
            onBlur();
        }
    };

    const handleDropdownOpenChange = (isOpen: boolean) => {
        setDropdownOpen(isOpen);
        if (!isOpen && mode === "range") {
            applyTempSelection();
        }
        if (!isOpen && mode === "single" && onBlur) {
            onBlur();
        }
    };

    // Détection mobile via media query (<= 768px)
    const [isMobile, setIsMobile] = useState<boolean>(() =>
        typeof window !== "undefined"
            ? window.matchMedia("(max-width: 1050px)").matches
            : false,
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(max-width: 1050px)");
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        if (mq.addEventListener) mq.addEventListener("change", handler);
        else mq.addListener(handler);
        return () => {
            if (mq.removeEventListener)
                mq.removeEventListener("change", handler);
            else mq.removeListener(handler);
        };
    }, []);

    useEffect(() => {
        if (!isMobile || mode !== "range") {
            setShowMobileRangeText(true);
            return;
        }

        const element = triggerRef.current;
        if (!element) return;

        const MIN_WIDTH_FOR_TEXT = 120;
        const update = () => {
            setShowMobileRangeText(element.clientWidth >= MIN_WIDTH_FOR_TEXT);
        };

        update();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(update);
            observer.observe(element);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", update);
        return () => window.removeEventListener("resize", update);
    }, [isMobile, mode]);

    const daysStrings = ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"];
    const reformmat = {
        ...fr,
        localize: {
            ...fr.localize,
            day: (values: any) => {
                return daysStrings[values];
            },
        },
    };

    // Initialiser :
    // - Mode single : toujours afficher le mois courant
    // - Mode range mobile : afficher le mois courant (1 seul mois)
    // - Mode range desktop : afficher mois -1 pour afficher le mois courant en second mois
    const [month, setMonth] = useState<Date>(
        mode === "single" || isMobile
            ? new Date(dateNow.getFullYear(), dateNow.getMonth(), 1)
            : new Date(dateNow.getFullYear(), dateNow.getMonth() - 1, 1),
    );

    const endMonth = useMemo(() => {
        return getLastDayOfSpecificMonth(
            dateNow.getFullYear(),
            dateNow.getMonth(),
        );
    }, [dateNow]);

    // Calculer le second mois affiché (pour la caption de droite)
    const secondMonth = useMemo(() => {
        return new Date(month.getFullYear(), month.getMonth() + 1, 1);
    }, [month]);

    // Etats temporaires pour la sélection en mobile (valider après)
    const [tempStart, setTempStart] = useState<Date | undefined>(startDate);
    const [tempEnd, setTempEnd] = useState<Date | undefined>(endDate);

    // Quand on ouvre en mobile, initialiser les valeurs temporaires
    useEffect(() => {
        if ((open && isMobile) || (dropdownOpen && !isMobile)) {
            setTempStart(startDate);
            setTempEnd(endDate);
        }
    }, [open, dropdownOpen, isMobile, startDate, endDate]);

    useEffect(() => {
        if (mode === "single" && !open) {
            onBlur?.();
        }
    }, [onBlur, mode, open]);

    const applyRangeSelection = useCallback((
        start: Date,
        end: Date = dateNow,
        commit: boolean = false,
    ) => {
        setTempStart(start);
        setTempEnd(end);
        if (commit) {
            setStartDate?.(start);
            setEndDate?.(end);
        }
    }, [dateNow, setStartDate, setEndDate]);

    const handleChangeDate = useMemo(
        () =>
            (start: Date, end: Date = dateNow) => {
                if (mode === "range") {
                    applyRangeSelection(start, end, false);
                } else {
                    setDate?.(start);
                }
            },
        [dateNow, mode, setDate, applyRangeSelection],
    );

    const shortcuts = useMemo(
        () => [
            {
                label: "Aujourd'hui",
                start: dateNow,
                end: dateNow,
            },
            {
                label: "Cette semaine",
                start: getFirstDayOfWeek(dateNow),
                end: new Date(getFirstDayOfWeek(dateNow).getFullYear(), getFirstDayOfWeek(dateNow).getMonth(), getFirstDayOfWeek(dateNow).getDate() + 6),
            },
            {
                label: "Ce mois",
                start: getFirstDayOfSpecificMonth(
                    dateNow.getFullYear(),
                    dateNow.getMonth(),
                ),
                end: getLastDayOfSpecificMonth(
                    dateNow.getFullYear(),
                    dateNow.getMonth(),
                ),
            },
            {
                label: "M-1",
                start: getFirstDayOfSpecificMonth(
                    dateNow.getFullYear(),
                    dateNow.getMonth() - 1,
                ),
                end: getLastDayOfSpecificMonth(
                    dateNow.getFullYear(),
                    dateNow.getMonth() - 1,
                ),
            },
            {
                label: "M-2",
                start: getFirstDayOfSpecificMonth(
                    dateNow.getFullYear(),
                    dateNow.getMonth() - 2,
                ),
                end: getLastDayOfSpecificMonth(
                    dateNow.getFullYear(),
                    dateNow.getMonth() - 2,
                ),
            },
            {
                label: "Cette année",
                start: getFirstDayOfSpecificYear(dateNow.getFullYear()),
                end: getLastDayOfSpecificMonth(
                    dateNow.getFullYear(),
                    dateNow.getMonth(),
                ),
            },
            {
                label: "Année précédente",
                start: getFirstDayOfSpecificYear(dateNow.getFullYear() - 1),
                end: getLastDayOfSpecificYear(dateNow.getFullYear() - 1),
            },
            {
                label: "Tout l'historique",
                start: getFirstDayOfSpecificYear(2023),
                end: dateNow,
            },
        ],
        [dateNow],
    );
    const classNames: Partial<ClassNames> = {
        day: styles.day,
        day_button: styles.day_button,
        month_caption: styles.month_caption,
        months: styles.months,
        month_grid: styles.month_grid,
        outside: styles.outside,
        selected: mode === "single" ? styles.selected : undefined,
        range_start: styles.range_start,
        range_middle: styles.range_middle,
        range_end: styles.range_end,
        weekday: styles.weekday,
        disabled: styles.disabled,
        hidden: styles.hidden,
    };

    // Composant DayPicker réutilisable
    const renderDayPicker = (
        numberOfMonths: number,
        useTemp: boolean = false,
    ) => {
        // useTemp s'applique seulement pour le mode range
        const shouldUseTemp = useTemp && mode === "range";
        const selectedStart = shouldUseTemp ? tempStart : startDate;
        const selectedEnd = shouldUseTemp ? tempEnd : endDate;

        const handleSelectDate = (date: any) => {
            if (mode === "single") {
                if (date) {
                    setDate?.(date);
                }
            } else {
                if (date?.from && date?.to) {
                    if (shouldUseTemp) {
                        setTempStart(date.from);
                        setTempEnd(date.to);
                    } else {
                        setStartDate?.(date.from);
                        setEndDate?.(date.to);
                    }
                }
            }
        };

        return mode === "single" ? (
            <DayPicker
                numberOfMonths={numberOfMonths}
                navLayout="around"
                hideNavigation
                month={month}
                endMonth={endMonth}
                locale={reformmat}
                mode={"single"}
                selected={date}
                onSelect={handleSelectDate}
                classNames={classNames}
                disabled={(date) => {
                    if (min && date < min) return true;
                    if (max && date > max) return true;
                    if (disableWeekends) {
                        const day = date.getDay();
                        if (day === 0 || day === 6) return true;
                    }
                    return false;
                }}
            />
        ) : (
            <DayPicker
                numberOfMonths={numberOfMonths}
                navLayout="around"
                hideNavigation
                month={month}
                endMonth={endMonth}
                showOutsideDays
                locale={reformmat}
                mode="range"
                selected={{ from: selectedStart, to: selectedEnd }}
                onSelect={handleSelectDate}
                classNames={classNames}
                disabled={(date) => {
                    if (min && date < min) return true;
                    if (max && date > max) return true;
                    return false;
                }}
            />
        );
    };

    const handlePreviousMonth = () => {
        if (month) {
            const prevMonth = new Date(
                month.getFullYear(),
                month.getMonth() - 1,
            );
            setMonth(prevMonth);
        }
    };

    const handleNextMonth = () => {
        if (month) {
            const nextMonth = new Date(
                month.getFullYear(),
                month.getMonth() + 1,
            );
            if (isMobile || mode === "single") {
                if (nextMonth.getTime() <= endMonth.getTime()) {
                    setMonth(nextMonth);
                }
            } else {
                const nextSecondMonth = new Date(
                    nextMonth.getFullYear(),
                    nextMonth.getMonth() + 1,
                    1,
                );
                if (nextSecondMonth.getTime() <= endMonth.getTime()) {
                    setMonth(nextMonth);
                }
            }
        }
    };

    // Contenu shared pour desktop
    const desktopContent = (
        <div className={styles.dropdown_content}>
            <div className={styles.calendar_container}>
                {mode === "range" &&
                    ((dropdownOpen ? tempStart : startDate) ||
                        (dropdownOpen ? tempEnd : endDate)) && (
                    <div className={styles.selected_date_range}>
                        <Text variant="body-bold">
                            {(dropdownOpen ? tempStart : startDate) &&
                                `${getShortFrDate(new Date((dropdownOpen ? tempStart : startDate)!))}`}
                        </Text>
                        <Text variant="body-bold">
                            {(dropdownOpen ? tempEnd : endDate) &&
                                getShortFrDate(
                                    new Date(
                                        (dropdownOpen ? tempEnd : endDate)!,
                                    ),
                                )}
                        </Text>
                    </div>
                )}
                {mode === "single" && (
                    <div className={styles.calendar_navigation}>
                        <div className={styles.month_caption_container}>
                            <div className={styles.left_arrow}>
                                <IconButton
                                    size="small"
                                    icon={ArrowLeftV2Icon}
                                    onClick={handlePreviousMonth}
                                />
                            </div>
                            <Text
                                variant="body-bold"
                                className={styles.caption}
                            >
                                {getMonthYearLabel(month)}
                            </Text>
                            <div className={styles.right_arrow}>
                                <IconButton
                                    size="small"
                                    icon={ArrowRightV2Icon}
                                    onClick={handleNextMonth}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {mode === "range" && (
                    <div className={styles.calendar_navigation}>
                        <div className={styles.month_caption_container}>
                            <div className={styles.left_arrow}>
                                <IconButton
                                    size="small"
                                    icon={ArrowLeftV2Icon}
                                    onClick={handlePreviousMonth}
                                />
                            </div>
                            <Text
                                variant="body-bold"
                                className={styles.caption}
                            >
                                {getMonthYearLabel(month)}
                            </Text>
                            <div className={styles.empty_nav_button}></div>
                        </div>
                        <div className={styles.month_caption_container}>
                            <div className={styles.empty_nav_button}></div>
                            <Text
                                variant="body-bold"
                                className={styles.caption}
                            >
                                {getMonthYearLabel(secondMonth)}
                            </Text>
                            <div className={styles.right_arrow}>
                                <IconButton
                                    size="small"
                                    icon={ArrowRightV2Icon}
                                    onClick={handleNextMonth}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {renderDayPicker(mode === "single" ? 1 : 2, mode === "range")}
                {mode === "range" && (
                    <div className={styles.validate_row}>
                        <Button
                            size="newSmall"
                            variant="primary"
                            label="Valider l'intervalle"
                            onClick={() => {
                                applyTempSelection();
                                setDropdownOpen(false);
                            }}
                            className={styles.validate_button}
                        />
                    </div>
                )}
            </div>
            {mode === "range" && (
                <>
                    <Divider orientation="vertical" />
                    <div className={styles.shortcut_buttons}>
                        {shortcuts.map(({ label, start, end }, index) => (
                            <div
                                key={index}
                                className={styles.shortcut_button}
                                onClick={() => handleChangeDate(start, end)}
                            >
                                <Button
                                    className={styles.shortcut_button}
                                    size="medium"
                                    variant="transparent"
                                    labelOptions={{
                                        align: "left",
                                        noWrap: true,
                                    }}
                                >
                                    {label}
                                </Button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );

    // Contenu mobile (dans une Modal) : un seul calendrier et boutons Valider/Annuler
    const mobileContent = (
        <div className={styles.modal_container}>
            {mode === "range" && (tempStart || tempEnd) && (
                <div className={styles.selected_date_range}>
                    <Text variant="body-bold">
                        {tempStart && `${getShortFrDate(new Date(tempStart))}`}
                    </Text>
                    <Text variant="body-bold">
                        {tempEnd && `${getShortFrDate(new Date(tempEnd))}`}
                    </Text>
                </div>
            )}
            {mode === "single" && (
                <div className={styles.selected_date_range}>
                    <Text variant="body-bold">
                        {date && `${getShortFrDate(new Date(date))}`}
                    </Text>
                </div>
            )}
            <Divider className={styles.divider} />
            <div className={styles.modal_body}>
                <div className={styles.modal_body_left}>
                    <div className={styles.calendar_navigation}>
                        <div className={styles.month_caption_container}>
                            <div className={styles.left_arrow}>
                                <IconButton
                                    size="small"
                                    icon={ArrowLeftV2Icon}
                                    onClick={handlePreviousMonth}
                                />
                            </div>
                            <Text
                                variant="body-bold"
                                className={styles.caption}
                            >
                                {getMonthYearLabel(month)}
                            </Text>
                            <div className={styles.right_arrow}>
                                <IconButton
                                    size="small"
                                    icon={ArrowRightV2Icon}
                                    onClick={handleNextMonth}
                                />
                            </div>
                        </div>
                    </div>
                    {renderDayPicker(1, true)}
                    {mode === "range" && (
                        <div className={styles.mobile_actions}>
                            <Button
                                size="medium"
                                variant="transparent"
                                onClick={() => setOpen(false)}
                            >
                                Annuler
                            </Button>
                            <Button
                                size="medium"
                                variant="primary"
                                onClick={() => {
                                    applyTempSelection();
                                    setOpen(false);
                                }}
                            >
                                Valider
                            </Button>
                        </div>
                    )}
                </div>
                {mode === "range" && (
                    <>
                        <Divider orientation="vertical" />
                        <div className={styles.mobile_shortcut_buttons}>
                            {shortcuts.map(({ label, start, end }, index) => (
                                <div
                                    key={index}
                                    className={styles.mobile_shortcut_button}
                                    onClick={() => {
                                        applyRangeSelection(start, end, true);
                                        setOpen(false);
                                    }}
                                >
                                    <Button
                                        className={styles.shortcut_button}
                                        size="medium"
                                        variant="transparent"
                                        labelOptions={{
                                            align: "left",
                                            noWrap: true,
                                        }}
                                    >
                                        {label}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    return (
        <div className={styles.root}>
            {isMobile && mode === "range" ? (
                <div
                    className={`${classNameTrigger || ""} ${
                        styles.triggerContainer
                    }`}
                >
                    <div
                        className={`${styles.date_picker}${
                            error ? ` ${styles.date_pickerError}` : ""
                        }`}
                        aria-invalid={Boolean(error)}
                        onClick={() => setOpen(true)}
                        ref={triggerRef}
                    >
                        {showMobileRangeText && (
                            <Text
                                variant="body-bold"
                                className={styles.trigger_label}
                            >
                                {startDate && endDate
                                    ? `${getShortDateFormat(new Date(startDate))} - ${getShortDateFormat(new Date(endDate))}`
                                    : "Sélectionner"}
                            </Text>
                        )}
                        <div className={styles.right_trigger}>
                            <CalendarIcon />
                        </div>
                    </div>
                    <Modal
                        open={open}
                        onClose={handleClose}
                        className={styles.calendar_modal}
                    >
                        {mobileContent}
                    </Modal>
                </div>
            ) : (
                <Dropdown
                    placement={placement}
                    open={mode === "range" ? dropdownOpen : undefined}
                    onOpenChange={handleDropdownOpenChange}
                    trigger={
                        <div
                            className={`${classNameTrigger || ""} ${
                                styles.triggerContainer
                            }`}
                        >
                            <div
                                className={`${styles.date_picker}${
                                    error ? ` ${styles.date_pickerError}` : ""
                                }`}
                                aria-invalid={Boolean(error)}
                                onClick={() => setOpen(true)}
                            >
                                <CalendarIcon />
                                <Text
                                    variant="body-bold"
                                    className={styles.trigger_label}
                                >
                                    {mode === "range"
                                        ? startDate && endDate
                                            ? `${getShortDateFormat(new Date(startDate))} - ${getShortDateFormat(new Date(endDate))}`
                                            : "Intervalle de dates"
                                        : date
                                          ? getShortFrDate(new Date(date))
                                          : "Sélectionner une date"}
                                </Text>
                            </div>
                        </div>
                    }
                >
                    {desktopContent}
                </Dropdown>
            )}
            <FieldError error={error} />
        </div>
    );
};

export default DatePicker;
